import { Menu, MenuItem, Divider, ListItemIcon, ListItemText } from "@mui/material"
import {useEffect, useState} from "react"
import {
    Visibility as ViewIcon,
    PictureAsPdf as PdfIcon,
    TableChart as ExcelIcon,
    Check as ApproveIcon,
    EditNote as ReviewIcon,
    Close as RejectIcon,
} from "@mui/icons-material"
import { useTranslation } from "react-i18next"
import {useUser} from "../../providers/user-context"
import axiosInstance from "../../config/axios-config"

const OrderActionsMenu = ({ anchorEl, open, onClose, order, onViewDetails, onApprove, onQuickApprove, onReject, onExport }) => {
    const { t, i18n } = useTranslation()
    const {tenantId, userIdentity, userRole, tenantType} = useUser()
    const [approval, setApproval] = useState(null)
    const lineId = order?.orderId
    const scope = `${tenantId}:${userIdentity}:${userRole}:${tenantType}:${i18n.language}:${lineId}:${open}`
    const eligible = open && Boolean(tenantId && userIdentity && lineId) && tenantType === "IMPORTER"
        && ["ADMIN", "OPERATOR", "SYSTEM"].includes(userRole) && order?.status === "PENDING" && !order?.courierProfileId
    // A submitted proposal can still have PENDING fulfillment; check the complete commercial decision.
    useEffect(() => {
        setApproval(null)
        if (!eligible) return
        const controller = new AbortController()
        axiosInstance.get(`/api/v1/supplier-orders/by-line/${lineId}`, {signal: controller.signal, timeout: 10000})
            .then(({data}) => {
                if (!controller.signal.aborted) setApproval({scope, outcome: data.summary?.outcome})
            })
            .catch(() => {}) // Keep approval disabled when its authoritative state is unavailable.
        return () => controller.abort()
    }, [eligible, lineId, scope])
    const canApprove = eligible && approval?.scope === scope && approval.outcome === "UNREVIEWED"

    const handleViewDetails = () => {
        onViewDetails(order)
        onClose()
    }

    const handleApprove = () => {
        if (!canApprove) return
        onApprove(order)
        onClose()
    }

    const handleQuickApprove = () => {
        if (!canApprove) return
        onQuickApprove(order)
        onClose()
    }

    const handleReject = () => {
        onReject(order)
        onClose()
    }

    const handleExportPdf = () => {
        onExport("pdf", order)
        onClose()
    }

    const handleExportExcel = () => {
        onExport("excel", order)
        onClose()
    }

    return (
        <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: { minWidth: 180, borderRadius: 2, boxShadow: 3 },
            }}
        >
            <MenuItem onClick={handleViewDetails}>
                <ListItemIcon>
                    <ViewIcon />
                </ListItemIcon>
                <ListItemText>{t("ViewDetails")}</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleQuickApprove} disabled={!canApprove || !onQuickApprove}>
                <ListItemIcon>
                    <ApproveIcon color="success" />
                </ListItemIcon>
                <ListItemText>{t("QuickApproveAll")}</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleApprove} disabled={!canApprove || !onApprove}>
                <ListItemIcon>
                    <ReviewIcon />
                </ListItemIcon>
                <ListItemText>{t("ReviewWorkspaceTitle")}</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleReject } disabled={order && order?.status !== "PENDING"}>
                <ListItemIcon>
                    <RejectIcon color="error" />
                </ListItemIcon>
                <ListItemText>{t("RejectOrder")}</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleExportPdf}>
                <ListItemIcon>
                    <PdfIcon />
                </ListItemIcon>
                <ListItemText>{`${t("ExportAs")} PDF`}</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleExportExcel}>
                <ListItemIcon>
                    <ExcelIcon />
                </ListItemIcon>
                <ListItemText>{`${t("ExportAs")} Excel`}</ListItemText>
            </MenuItem>
        </Menu>
    )
}

export default OrderActionsMenu
